import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, Typography } from '@mui/material';
import React, { memo, useCallback, useMemo, useState } from 'react';

import { GetPitchbookCompanyRawDealsResponse } from '~/services/rtkQuery/company/types';

import { Round } from './components/Round';
import styles from './styles.module.scss';

export type MatchedRawDeals = Record<string, GetPitchbookCompanyRawDealsResponse['result']['data']>;

interface Props {
  visible: boolean;
  rawDeals: GetPitchbookCompanyRawDealsResponse['result']['data'];
  initialMatchedRawDeals: MatchedRawDeals | null;
  onClose: () => void;
  onSave: (data: MatchedRawDeals) => void;
}

const Component: React.FC<Props> = ({
  visible,
  rawDeals,
  initialMatchedRawDeals,
  onClose,
  onSave,
}) => {
  const [unmatchedRawDeals, setUnmatchedRawDeals] = useState(() => {
    const matchedRawDeals = Object.values(initialMatchedRawDeals || {}).flat();

    return initialMatchedRawDeals
      ? rawDeals.filter(
          rawDeal =>
            !matchedRawDeals.find(matchedRawDeal => matchedRawDeal.dealid === rawDeal.dealid),
        )
      : rawDeals;
  });
  const [matchedRawDeals, setMatchedRawDeals] = useState<MatchedRawDeals>(() => {
    return (
      initialMatchedRawDeals || {
        A: [],
        B: [],
        C: [],
      }
    );
  });

  const handleRawDealDrop = useCallback(
    (
      rawDeal: GetPitchbookCompanyRawDealsResponse['result']['data'][number],
      dragRoundName?: string,
      dropRoundName?: string,
    ) => {
      setUnmatchedRawDeals(prevValue => {
        if (!dragRoundName) {
          return prevValue.filter(item => item.dealid !== rawDeal.dealid);
        }

        if (!dropRoundName) {
          return [...prevValue, rawDeal];
        }

        return prevValue;
      });

      setMatchedRawDeals(prevValue => {
        const copiedPrevValue = { ...prevValue };

        if (dragRoundName) {
          copiedPrevValue[dragRoundName] = copiedPrevValue[dragRoundName].filter(
            item => item.dealid !== rawDeal.dealid,
          );
        }

        if (dropRoundName) {
          copiedPrevValue[dropRoundName] = [...copiedPrevValue[dropRoundName], rawDeal];
        }

        return copiedPrevValue;
      });
    },
    [],
  );

  const anyRawDealMatched = useMemo(() => {
    return Object.values(matchedRawDeals).flat().length > 0;
  }, [matchedRawDeals]);

  return (
    <Dialog fullWidth maxWidth="xl" open={visible} onClose={onClose}>
      <div className={styles.modal}>
        <Typography color="text.primary" mb="36px" variant="h5">
          Match Rounds
        </Typography>
        <button aria-label="Close" className={styles.modal__close} type="button" onClick={onClose}>
          <CloseIcon sx={{ width: '18px', height: '18px', color: 'var(--gray-2-color)' }} />
        </button>
        <Typography color="text.primary" fontWeight="300" mb="16px" variant="subtitle1">
          Rounds from Pitchbook
        </Typography>
        <Round rawDeals={unmatchedRawDeals} onRawDealDrop={handleRawDealDrop} />
        <Typography color="text.primary" fontWeight="300" mb="30px" mt="32px" variant="subtitle1">
          Neom Rounds
        </Typography>
        {Object.keys(matchedRawDeals).map((roundName, index) => (
          <Round
            className={
              index !== Object.keys(matchedRawDeals).length - 1 ? styles.modal__round : undefined
            }
            key={roundName}
            name={roundName}
            rawDeals={matchedRawDeals[roundName]}
            onRawDealDrop={handleRawDealDrop}
          />
        ))}
        <div className={styles.modal__buttons}>
          <Button color="secondary" sx={{ width: '308px', mr: '16px' }} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!anyRawDealMatched}
            sx={{ width: '308px' }}
            onClick={() => onSave(matchedRawDeals)}
          >
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export const MatchRoundsModal = memo(Component);
